import { useState, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Feature image slideshow (auto-advancing, mirrors PropertyOverview)
// ============================================================================

function FeatureSlideshow({ images }: { images: PhotoGallery[] }) {
  const [current, setCurrent] = useState(0);
  const count = images.length;
  const safeCurrent = count > 0 ? Math.min(current, count - 1) : 0;

  // Auto-advance every 5s; pauses when there is 0 or 1 slide.
  useEffect(() => {
    if (count <= 1) return;
    const id = window.setInterval(() => {
      setCurrent((p) => (p + 1) % count);
    }, 5000);
    return () => window.clearInterval(id);
  }, [count]);

  if (count === 0) {
    return <div className="fp-feature-image fp-feature-image--empty" />;
  }

  return (
    <div className="fp-feature-image">
      {images.map((img, i) => (
        <div
          key={img.image || img.original_url || i}
          className={`fp-feature-slide${i === safeCurrent ? ' is-active' : ''}`}
          aria-hidden={i === safeCurrent ? undefined : true}
        >
          <VibesitesImage
            url={img.image || img.original_url || ''}
            alt={img.alt_text || img.title || 'Community photo'}
            className="w-full h-full object-cover object-center"
            sizes="(max-width: 400px) 100vw, 400px"
            priority={i === 0}
          />
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Slot group definitions
// ============================================================================

// "View All Layouts" button (filled)
const viewAllCtaDef: SlotGroupDefinition = {
  label: 'View All Button',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View All Layouts' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// "Contact Us" button (outlined)
const contactCtaDef: SlotGroupDefinition = {
  label: 'Contact Button',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/contact' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Contact Us' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  // Matches Duda home collage: Photo Gallery title = Community, first 4.
  const featurePhotos = useCollectionBinding<PhotoGallery>('floorplanslist.gallery', {
    collection: 'photo_gallery',
    defaultLimit: 4,
    filter: { title: 'Community' },
  });

  const viewAllCta = useSlotGroup('floorplanslist.view_all_cta', viewAllCtaDef);
  const contactCta = useSlotGroup('floorplanslist.contact_cta', contactCtaDef);

  useRevealStyles();
  const imageRef = useScrollReveal<HTMLDivElement>();
  const blurbRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="fp-section pt-12 md:pt-20 pb-12 md:pb-20 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="fp-grid grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">

          {/* ── Left: feature image slideshow ── */}
          <div ref={imageRef} className="fp-media reveal-fade-up order-1 md:pl-8 lg:pl-12">
            <FeatureSlideshow images={featurePhotos ?? []} />
          </div>

          {/* ── Right: heading + blurb + buttons ── */}
          <div className="fp-text order-2 text-center md:text-left md:pr-8 lg:pr-12">
            <RichTextContent
              path="floorplanslist.heading"
              label="Section Heading"
              defaultValue="<p>Find Your Perfect Space</p>"
              as="h2"
              className="fp-section-title text-3xl lg:text-4xl font-heading leading-tight whitespace-nowrap mb-8 md:mb-10"
            />

            {/* Rendered exactly like PropertyOverview's description so the two
                blurbs share the same type treatment. */}
            <div ref={blurbRef} className="reveal-fade-up mb-10 md:mb-12">
              <RichTextContent
                path="floorplanslist.blurb"
                label="Blurb"
                as="div"
                className="fp-blurb font-body text-[0.875rem] sm:text-[1rem] tracking-normal text-[var(--vibe-color-text)] [&_a]:text-primary [&_a]:underline"
                style={{ lineHeight: 2 }}
              />
            </div>

            <div className="flex flex-col md:flex-row justify-center md:justify-start gap-4">
              <EditableSlotGroup groupPath="floorplanslist.view_all_cta" definition={viewAllCtaDef} className="block w-full md:flex-1 md:min-w-0">
                <a
                  href={viewAllCta.url || '/floor-plans'}
                  target={viewAllCta._target || '_self'}
                  className="fp-btn fp-btn--filled inline-flex items-center justify-center w-full whitespace-nowrap text-sm px-8 py-5 transition-all duration-300"
                >
                  {viewAllCta.label || 'View All Layouts'}
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="floorplanslist.contact_cta" definition={contactCtaDef} className="block w-full md:flex-1 md:min-w-0">
                <a
                  href={contactCta.url || '/contact'}
                  target={contactCta._target || '_self'}
                  className="fp-btn fp-btn--outline inline-flex items-center justify-center w-full whitespace-nowrap text-sm px-8 py-5 transition-all duration-300"
                >
                  {contactCta.label || 'Contact Us'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        .fp-section-title {
          color: var(--vibe-color-text);
        }
        /* Match PropertyOverview blurbs: sans body face + airy leading. */
        .fp-blurb,
        .fp-blurb p,
        .fp-blurb span {
          font-family: var(--vibe-font-body) !important;
          letter-spacing: normal !important;
          line-height: 2 !important;
        }
        /* "Find Your Perfect Space" must stay on a single line; scale the
           type down wherever the column is too narrow (phones + tablet
           two-col) so it never wraps or overflows. */
        @media (max-width: 480px) {
          .fp-section-title {
            font-size: 1.75rem;
          }
        }
        @media (min-width: 768px) and (max-width: 1023px) {
          .fp-section-title {
            font-size: 1.5rem;
          }
        }

        /* Square feature slideshow — max 400px wide, centered, soft shadow. */
        .fp-feature-image {
          position: relative;
          width: 100%;
          max-width: 400px;
          margin-left: auto;
          margin-right: auto;
          aspect-ratio: 1 / 1;
          overflow: hidden;
          background: var(--vibe-color-surface);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
        }
        /* Cross-fading slides stacked inside the fixed square frame. */
        .fp-feature-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.6s ease;
        }
        .fp-feature-slide.is-active {
          opacity: 1;
          pointer-events: auto;
          z-index: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          .fp-feature-slide { transition: none; }
        }

        /* Match header / Community Info CTAs: body face, normal weight, tight tracking. */
        .fp-btn {
          border-radius: 0;
          border: 1px solid #000;
          font-family: var(--vibe-font-body) !important;
          font-weight: 400;
          letter-spacing: 0.03em;
        }
        .fp-btn--filled {
          background: #000;
          color: #fff;
        }
        .fp-btn--filled:hover {
          background: #fff;
          color: #000;
        }
        .fp-btn--outline {
          background: #000;
          color: #fff;
        }
        .fp-btn--outline:hover {
          background: #fff;
          color: #000;
        }

        /* Tablet: same rebalance as PropertyOverview — keep the 50/50 split and
           the image-left order, but stop spending ~15% of an already narrow row
           on the desktop gutter and the inset padding. The photo gains the most
           here since its 400px cap never binds at this width. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .fp-section .fp-grid { gap: 2rem; }
          .fp-section .fp-media { padding-left: 0; }
          .fp-section .fp-text { padding-right: 0; }
          /* Airy desktop leading reads as slack at a short measure. */
          .fp-section .fp-blurb,
          .fp-section .fp-blurb p,
          .fp-section .fp-blurb span {
            line-height: 1.8 !important;
          }
          /* The paired nowrap buttons split one column, so their desktop
             padding can push the labels past the flex-1 track. Trimming it
             costs nothing visually — w-full sizing comes from flex-1. */
          .fp-section .fp-btn {
            padding-left: 1rem;
            padding-right: 1rem;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Find Your Perfect Space</p>',
    },
    'floorplanslist.blurb': {
      label: 'Blurb',
      type: 'richtext',
      defaultField: 'property_home_floorplans_blurb',
      defaultValue: '<p>Discover thoughtfully designed floor plans made for the way you live. From open studios to spacious multi-bedroom layouts, find the space that feels like home.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.view_all_cta': viewAllCtaDef,
    'floorplanslist.contact_cta': contactCtaDef,
  },
  collectionBindings: {
    'floorplanslist.gallery': {
      label: 'Feature Slideshow Photos',
      collection: 'photo_gallery',
      defaultLimit: 4,
      filter: { title: 'Community' },
      description: 'Slideshow images. Shows photo gallery items whose title is Community (up to 4).',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
